import { useState } from 'react';
import {
  useUnassignedDevices, deleteUnassignedDevice,
  assignDevice, generateResidentUid, saveResident,
} from '@/shared/firebase/admin';
import { useAuth } from '@/shared/auth/AuthContext';
import { useTrackedResidents } from '@/shared/hooks/useTracked';
import { Modal } from '@/shared/components/Modal';
import { timeAgo } from '@/shared/utils/time';

export function DevicesTab() {
  const { devices, loading } = useUnassignedDevices();
  const [assigning, setAssigning] = useState<string | null>(null);

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div>
        <h2 className="text-xl font-extrabold text-slate-900">Dispositivos sin asignar</h2>
        <p className="text-sm text-slate-500">
          Aparecen aquí los dispositivos Traccar que han enviado señal pero no están vinculados a ningún residente.
        </p>
      </div>

      {loading ? (
        <p className="text-slate-500">Cargando…</p>
      ) : devices.length === 0 ? (
        <div className="card text-center py-10">
          <p className="text-slate-500">No hay dispositivos pendientes.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {devices.map((d) => (
            <li key={d.imei} className="card flex items-center gap-3">
              <span className="text-2xl" aria-hidden>📡</span>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-slate-900 truncate">IMEI <code>{d.imei}</code></p>
                <p className="text-xs text-slate-500 truncate">
                  {d.deviceName || 'Sin nombre'}
                  {d.lastSeen && <> · vista {timeAgo(d.lastSeen)}</>}
                  {d.lastLat != null && d.lastLng != null && (
                    <> · {d.lastLat.toFixed(4)}, {d.lastLng.toFixed(4)}</>
                  )}
                </p>
              </div>
              <button onClick={() => setAssigning(d.imei)} className="btn-primary text-xs">Asignar</button>
              <button
                onClick={async () => {
                  if (confirm(`¿Borrar el dispositivo ${d.imei} de la lista?`)) {
                    await deleteUnassignedDevice(d.imei);
                  }
                }}
                className="btn-ghost text-xs"
              >Ocultar</button>
            </li>
          ))}
        </ul>
      )}

      {assigning && (
        <AssignDialog
          imei={assigning}
          deviceName={devices.find((d) => d.imei === assigning)?.deviceName}
          onClose={() => setAssigning(null)}
        />
      )}
    </div>
  );
}

function AssignDialog({ imei, deviceName, onClose }: { imei: string; deviceName?: string; onClose: () => void }) {
  const { centerId } = useAuth();
  const { residents } = useTrackedResidents(centerId);
  const [mode, setMode] = useState<'existing' | 'new'>('new');
  const [residentUid, setResidentUid] = useState('');
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setSaving(true); setErr(null);
    try {
      let uid = residentUid;
      if (mode === 'new') {
        if (!newName.trim()) { setErr('Pon un nombre al residente'); setSaving(false); return; }
        uid = generateResidentUid();
        await saveResident(centerId, uid, { name: newName });
      }
      if (!uid) { setErr('Selecciona el residente'); setSaving(false); return; }
      await assignDevice(centerId, uid, { imei, deviceName });
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setSaving(false); }
  }

  return (
    <Modal onClose={onClose}>
      <h3 className="text-lg font-bold mb-1">Asignar dispositivo</h3>
      <p className="text-xs text-slate-500 mb-4">IMEI <code>{imei}</code></p>

      <div className="space-y-3">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setMode('new')}
            className={`flex-1 rounded-xl border py-2 text-sm font-semibold transition
              ${mode==='new' ? 'bg-brand text-white border-brand' : 'bg-white border-slate-200'}`}
          >Crear residente nuevo</button>
          <button
            type="button"
            onClick={() => setMode('existing')}
            className={`flex-1 rounded-xl border py-2 text-sm font-semibold transition
              ${mode==='existing' ? 'bg-brand text-white border-brand' : 'bg-white border-slate-200'}`}
          >Vincular a uno existente</button>
        </div>

        {mode === 'new' ? (
          <div>
            <label className="label">Nombre del residente</label>
            <input className="input" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Ej. María García" />
          </div>
        ) : (
          <div>
            <label className="label">Residente</label>
            <select className="input" value={residentUid} onChange={(e) => setResidentUid(e.target.value)}>
              <option value="">— Selecciona —</option>
              {residents.map((r) => (
                <option key={r.uid} value={r.uid}>{r.config?.name || r.uid}</option>
              ))}
            </select>
          </div>
        )}

        {err && <p className="text-sm text-danger">{err}</p>}
        <div className="flex gap-2 pt-2">
          <button onClick={submit} disabled={saving} className="btn-primary flex-1">
            {saving ? 'Asignando…' : 'Asignar'}
          </button>
          <button onClick={onClose} className="btn-ghost">Cancelar</button>
        </div>
      </div>
    </Modal>
  );
}
